// CadAgentCard.tsx — the CAD agent (cad/AGENT.md §7): a server-side harness
// drives an LLM to write an OpenSCAD model of the scan, one named feature at
// a time, building and comparing each change with the scan.
//
// The LLM is billed to the user's inference account (their own inference.use
// token, AGENT.md §5). A token without inference.use gets "Connect inference
// account", which signs in again asking for it (and keeps openscan.compute
// for a user who had it). Viewers see the runs read-only; editors with the
// scope get the start form: model, budget, instructions and an optional
// revision to start from.
//
// A run shows its status, cost so far against the budget, turns and latest
// F-score, then a timeline collapsed to builds, renders, subagent spans and
// the finish ("Show all" expands every step). Clicking a step opens the
// inspector: its images and text, the .scad as it was at that step with a
// feature's lines marked, and buttons that render that step's file whole,
// up to a feature, only a feature, or with the feature highlighted, over the
// scan or not — the same partial renders the agent uses to debug. A running
// job is polled every 3 s for new steps (the viewer's live socket is only
// open while a scan captures or refines).
import { useEffect, useMemo, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import { BsBoxArrowInRight, BsDownload, BsPlayFill, BsStopFill } from 'react-icons/bs';
import { Badge } from 'common';
import Dropdown from 'common/ui/Dropdown';
import { ApiError, agentRunning, api, atLeast, featuresOf, hasCompute, hasInference, inferenceLoginUrl, urls } from '../api';
import type { AgentCuboid, AgentJob, AgentModel, AgentPart, AgentPlanItem, AgentRenderMode, AgentStep, CADRevision, Me, Scan } from '../api';
import { errorMessage } from '../format';

// POLL_MS is how often a running job is re-read.
export const POLL_MS = 3000;
// BUDGETS are the card's budget choices (USD); the server caps at 5.
export const BUDGETS = [0.25, 0.5, 1, 2] as const;
// DEFAULT_MODEL is used until the server's model list arrives.
export const DEFAULT_MODEL = 'openrouter/deepseek/deepseek-v4.1-flash';
// KEY_KINDS are the step kinds the collapsed timeline shows.
const KEY_KINDS = new Set(['build', 'render', 'subagent_start', 'subagent_done', 'finish', 'error', 'plan', 'revert']);

export interface CadAgentCardProps {
  scan: Scan;
  onError: (msg: string) => void;
  // signedIn is false for a guest on a share link (no start form).
  signedIn?: boolean;
  // onRevision is told when a run stores a new revision (the model view can reload).
  onRevision?: (rev: number) => void;
}

function currentPath(): string {
  return window.location.pathname + window.location.search;
}

function usd(v: number | undefined): string {
  const x = v ?? 0;
  return x < 0.1 ? `$${x.toFixed(3)}` : `$${x.toFixed(2)}`;
}

function statusTone(s: AgentJob['status']): 'default' | 'info' | 'warning' {
  if (s === 'running' || s === 'queued') return 'info';
  if (s === 'failed' || s === 'budget') return 'warning';
  return 'default';
}

const PLAN_LABEL: Record<string, string> = {
  pending: 'to do',
  running: 'in progress',
  kept: 'kept',
  reverted: 'reverted',
  failed: 'failed',
};

function planTone(s: string): 'default' | 'info' | 'warning' {
  if (s === 'running') return 'info';
  if (s === 'reverted' || s === 'failed') return 'warning';
  return 'default';
}

// PlanChecklist is a guided run's plan: each feature, its kind, and what
// became of it (kept/reverted and the change in fit).
function PlanChecklist({ plan }: { plan: AgentPlanItem[] }) {
  return (
    <ol className="list-unstyled small mb-2" data-testid="agent-plan">
      {plan.map((p) => (
        <li key={p.name} className="d-flex flex-wrap align-items-baseline gap-2 py-1" title={p.note || p.what}>
          <Badge tone={planTone(p.status)}>{PLAN_LABEL[p.status] ?? p.status}</Badge>
          <span className="fw-semibold">{p.name}</span>
          <span className="text-body-secondary">{p.kind}</span>
          {(p.status === 'kept' || p.status === 'reverted') && p.delta_f !== 0 && (
            <span className="text-body-secondary">
              ΔF {p.delta_f > 0 ? '+' : ''}
              {p.delta_f.toFixed(3)}
            </span>
          )}
          <span className="text-body-secondary text-truncate" style={{ maxWidth: '100%' }}>
            {clip(p.what, 90)}
          </span>
        </li>
      ))}
    </ol>
  );
}

// InventoryList is a guided run's inventory: the cuboids it looked inside
// and every part it identified (label, kind, centre), collapsed by default.
function InventoryList({ cuboids, parts }: { cuboids: AgentCuboid[]; parts: AgentPart[] }) {
  const centre = (p: AgentPart) => p.min.map((v, i) => ((v + p.max[i]) / 2).toFixed(1)).join(', ');
  return (
    <details className="small mb-2" data-testid="agent-inventory">
      <summary>
        Inventory: {parts.length} part{parts.length === 1 ? '' : 's'} in {cuboids.length} cuboid{cuboids.length === 1 ? '' : 's'}
      </summary>
      <ul className="list-unstyled mb-1 mt-1">
        {cuboids.map((c) => (
          <li key={c.name} className="d-flex flex-wrap gap-2" title={c.note || c.look_for}>
            <Badge tone={c.status === 'running' ? 'info' : c.status === 'done' ? 'default' : 'warning'}>{c.status}</Badge>
            <span className="fw-semibold">{c.name}</span>
            <span className="text-body-secondary">
              {c.feature || '—'} · {c.views.join('+')} · {c.items} part{c.items === 1 ? '' : 's'}
            </span>
          </li>
        ))}
      </ul>
      <ol className="list-unstyled mb-0" style={{ columns: '14em' }}>
        {parts.map((p) => (
          <li key={p.id} title={`${p.kind} in ${p.cuboid}${p.snapped ? ' (measured bump)' : ''}: centre ${centre(p)} mm`}>
            <span className="text-body-secondary">#{p.id}</span> {p.label || '—'}{' '}
            <span className="text-body-secondary">{p.kind}</span>
          </li>
        ))}
      </ol>
    </details>
  );
}

const STATUS_LABEL: Record<string, string> = {
  queued: 'Queued',
  running: 'Running',
  done: 'Done',
  failed: 'Failed',
  cancelled: 'Cancelled',
  budget: 'Budget reached',
};

// isSub reports a subagent's step; subFeature names its feature.
function isSub(st: AgentStep): boolean {
  return st.agent.startsWith('sub:');
}
function subFeature(st: AgentStep): string {
  return st.agent.replace(/^sub:/, '').replace(/#\d+$/, '');
}

function stepLabel(st: AgentStep): string {
  switch (st.kind) {
    case 'build':
      return `Build${st.revision ? ` → revision ${st.revision}` : ''}`;
    case 'render':
      return 'Render';
    case 'subagent_start':
      return `Subagent started on ${subFeature(st) || 'a feature'}`;
    case 'subagent_done':
      return `Subagent finished ${subFeature(st) || ''}`.trim();
    case 'finish':
      return 'Finished';
    case 'error':
      return 'Error';
    case 'edit':
      return `Edit${st.tool ? ` (${st.tool})` : ''}`;
    case 'tool':
      return st.tool || 'Tool';
    case 'note':
      return 'Journal';
    case 'survey':
      return 'Survey';
    case 'plan':
      return 'Plan';
    case 'revert':
      return 'Reverted';
    default:
      return 'Thinking';
  }
}

function clip(s: string | undefined, n: number): string {
  if (!s) return '';
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

// ---- step inspector ----

interface InspectorProps {
  scan: Scan;
  job: AgentJob;
  step: AgentStep;
  fileAt: string; // the step's file, or the newest earlier one ('' = none)
}

const MODES: { key: string; label: string; needsFeature: boolean }[] = [
  { key: 'model', label: 'Model', needsFeature: false },
  { key: 'upto', label: 'Up to', needsFeature: true },
  { key: 'only', label: 'Only', needsFeature: true },
  { key: 'highlight', label: 'Highlight', needsFeature: true },
];

function StepInspector({ scan, job, step, fileAt }: InspectorProps) {
  const [source, setSource] = useState('');
  const [feature, setFeature] = useState('');
  const [overlay, setOverlay] = useState<'none' | 'overlay' | 'deviation'>('none');
  const [render, setRender] = useState<{ url: string; label: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    setSource('');
    setErr('');
    if (!fileAt) return;
    api
      .agentFile(scan.id, job.id, fileAt)
      .then((t) => {
        if (!cancelled) setSource(t);
      })
      .catch((e) => {
        if (!cancelled) setErr(errorMessage(e));
      });
    return () => {
      cancelled = true;
    };
  }, [scan.id, job.id, fileAt]);

  const feats = useMemo(() => featuresOf(source), [source]);
  const names = feats.length ? feats.map((f) => f.name) : (step.features ?? []);
  useEffect(() => {
    if (!feature && names.length) setFeature(names[0]);
  }, [names, feature]);
  useEffect(() => () => {
    if (render) URL.revokeObjectURL(render.url);
  }, [render]);

  const marked = feats.find((f) => f.name === feature);

  const doRender = async (key: string) => {
    const mode = (key === 'model' ? 'model' : `${key}:${feature}`) as AgentRenderMode;
    setBusy(true);
    setErr('');
    try {
      const blob = await api.renderAgentStep(scan.id, job.id, { step: step.n, mode, scan: overlay });
      setRender({ url: URL.createObjectURL(blob), label: `step ${step.n}: ${mode}${overlay !== 'none' ? `, scan ${overlay}` : ''}` });
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-body-tertiary rounded p-2 mt-2" data-testid="step-inspector">
      <div className="small fw-semibold mb-1">
        Step {step.n} · {stepLabel(step)}
        {isSub(step) ? ` · subagent ${subFeature(step)}` : ''}
      </div>
      {(step.images ?? []).map((img) => (
        <img key={img} src={urls.agentImage(scan.id, job.id, img)} alt={`step ${step.n}`} className="img-fluid rounded mb-1" />
      ))}
      {step.text && <div className="small text-body-secondary mb-2" style={{ whiteSpace: 'pre-wrap' }}>{clip(step.text, 1200)}</div>}

      {fileAt ? (
        <>
          <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
            <Dropdown
              aria-label="Feature"
              value={feature}
              onChange={(e) => setFeature(e.target.value)}
              disabled={!names.length}
              minWidth={8}
              options={names.length ? names.map((n) => ({ value: n, label: n })) : [{ value: '', label: 'No features' }]}
            />
            <Dropdown
              aria-label="Scan"
              value={overlay}
              onChange={(e) => setOverlay(e.target.value as typeof overlay)}
              minWidth={10}
              options={[
                { value: 'none', label: 'Model only' },
                { value: 'overlay', label: 'Over the scan' },
                { value: 'deviation', label: 'Scan deviation' },
              ]}
            />
            <ButtonGroup size="sm">
              {MODES.map((m) => (
                <Button key={m.key} variant="outline-secondary" disabled={busy || (m.needsFeature && !feature)} onClick={() => void doRender(m.key)}>
                  {m.label}
                </Button>
              ))}
            </ButtonGroup>
          </div>
          {render && (
            <figure className="mb-2">
              <img src={render.url} alt={render.label} className="img-fluid rounded" data-testid="step-render" />
              <figcaption className="small text-body-secondary">{render.label}</figcaption>
            </figure>
          )}
          {source && (
            <pre className="small bg-body rounded p-1 mb-0" style={{ maxHeight: '16rem', overflow: 'auto' }} data-testid="step-source">
              {source.split('\n').map((line, i) => {
                const on = marked && i + 1 >= marked.start && i + 1 <= marked.end;
                return (
                  <div key={i} className={on ? 'bg-warning-subtle' : undefined}>
                    <span className="text-body-secondary user-select-none me-2">{String(i + 1).padStart(3, ' ')}</span>
                    {line}
                  </div>
                );
              })}
            </pre>
          )}
        </>
      ) : (
        <div className="small text-body-secondary">No model file yet at this step.</div>
      )}
      {err && <div className="small text-danger mt-1">{err}</div>}
    </div>
  );
}

// ---- the card ----

export function CadAgentCard({ scan, onError, signedIn = true, onRevision }: CadAgentCardProps) {
  const editor = atLeast(scan, 'editor');
  const foreign = scan.access !== undefined && scan.access !== 'owner';

  const [me, setMe] = useState<Me | null>(null);
  const [models, setModels] = useState<AgentModel[]>([]);
  const [model, setModel] = useState(DEFAULT_MODEL);
  const [invModel, setInvModel] = useState('');
  const [budget, setBudget] = useState<number>(0.5);
  const [instructions, setInstructions] = useState('');
  const [revisions, setRevisions] = useState<CADRevision[]>([]);
  const [startFrom, setStartFrom] = useState(0);
  const [jobs, setJobs] = useState<AgentJob[]>([]);
  const [jobId, setJobId] = useState('');
  const [job, setJob] = useState<AgentJob | null>(null);
  const [steps, setSteps] = useState<AgentStep[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [selected, setSelected] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [refused, setRefused] = useState(false);
  const lastRev = useRef(0);

  // Who is signed in, and whether their token has inference.use.
  useEffect(() => {
    if (!signedIn) return;
    let cancelled = false;
    api
      .me()
      .then((m) => {
        if (!cancelled) setMe(m);
      })
      .catch((e) => console.info('[openscan/agent] /api/me unavailable:', errorMessage(e)));
    return () => {
      cancelled = true;
    };
  }, [signedIn]);

  // Models, revisions and past runs of this scan.
  useEffect(() => {
    let cancelled = false;
    setJobs([]);
    setJobId('');
    setJob(null);
    setSteps([]);
    setSelected(null);
    api
      .agentModels(scan.id)
      .then((r) => {
        if (cancelled) return;
        setModels(r.models);
        if (r.default) setModel(r.default);
        setInvModel(r.inventoryDefault);
      })
      .catch((e) => console.info('[openscan/agent] model list unavailable:', errorMessage(e)));
    api
      .listCAD(scan.id)
      .then((r) => {
        if (!cancelled) setRevisions(r);
      })
      .catch(() => undefined);
    api
      .listAgentJobs(scan.id)
      .then((js) => {
        if (cancelled) return;
        setJobs(js);
        if (js.length) setJobId(js[0].id);
      })
      .catch((e) => console.info('[openscan/agent] runs unavailable:', errorMessage(e)));
    return () => {
      cancelled = true;
    };
  }, [scan.id]);

  // Load the selected job in full, then poll it while it runs.
  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    let timer = 0;
    let last = 0;
    const merge = (incoming: AgentStep[]) => {
      if (!incoming.length) return;
      last = Math.max(last, ...incoming.map((s) => s.n));
      setSteps((prev) => {
        const have = new Set(prev.map((s) => s.n));
        return [...prev, ...incoming.filter((s) => !have.has(s.n))].sort((a, b) => a.n - b.n);
      });
    };
    const tick = async () => {
      try {
        const d = await api.getAgentJob(scan.id, jobId, last);
        if (cancelled) return;
        setJob(d.job);
        setJobs((prev) => prev.map((j) => (j.id === d.job.id ? d.job : j)));
        merge(d.steps);
        if (d.job.revision && d.job.revision !== lastRev.current) {
          if (lastRev.current !== 0) onRevision?.(d.job.revision);
          lastRev.current = d.job.revision;
        }
        if (agentRunning(d.job)) timer = window.setTimeout(() => void tick(), POLL_MS);
      } catch (e) {
        if (!cancelled) {
          console.warn('[openscan/agent] poll failed:', errorMessage(e));
          timer = window.setTimeout(() => void tick(), POLL_MS * 2);
        }
      }
    };
    setSteps([]);
    setSelected(null);
    lastRev.current = 0;
    void tick();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [scan.id, jobId, onRevision]);

  const inference = hasInference(me) && !refused;
  const running = jobs.some(agentRunning);
  const priceOf = (m: AgentModel) => `$${m.usd_per_m_in.toFixed(3)}/M in · $${m.usd_per_m_out.toFixed(2)}/M out`;
  const modelOptions: AgentModel[] = models.length ? models : [{ id: DEFAULT_MODEL, label: 'DeepSeek V4.1 Flash', usd_per_m_in: 0.045, usd_per_m_out: 0.5 }];

  const start = async () => {
    setBusy(true);
    try {
      const j = await api.startAgent(scan.id, {
        model,
        // '' is "Same model": name it, since the server reads '' as its default.
        ...(models.some((m) => m.vision) ? { inventory_model: invModel || model } : {}),
        instructions: instructions.trim(),
        budget_usd: budget,
        ...(startFrom > 0 ? { start_from: startFrom } : {}),
      });
      console.info(`[openscan/agent] started ${j.id} on ${scan.id} with ${model}, budget $${budget}`);
      setJobs((prev) => [j, ...prev.filter((x) => x.id !== j.id)]);
      setJobId(j.id);
      setJob(j);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'inference_scope') setRefused(true);
      else onError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!job) return;
    setBusy(true);
    try {
      const j = await api.cancelAgent(scan.id, job.id);
      setJob(j);
      setJobs((prev) => prev.map((x) => (x.id === j.id ? j : x)));
    } catch (e) {
      onError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const shown = showAll ? steps : steps.filter((s) => KEY_KINDS.has(s.kind));
  const sel = selected !== null ? steps.find((s) => s.n === selected) : undefined;
  // The file as of a step: its own snapshot, else the newest earlier one.
  const fileAt = (n: number): string => {
    let f = '';
    for (const s of steps) {
      if (s.n > n) break;
      if (s.file) f = s.file;
    }
    return f;
  };
  const base = scan.name.replace(/[^\w.-]+/g, '_') || scan.id;

  return (
    <Card data-testid="cad-agent-card">
      <Card.Header className="d-flex align-items-center gap-2">
        CAD agent
        {job && (
          <Badge tone={statusTone(job.status)} data-testid="agent-status">
            {STATUS_LABEL[job.status] ?? job.status}
          </Badge>
        )}
      </Card.Header>
      <Card.Body>
        {jobs.length === 0 && !job && (
          <div className="small text-body-secondary">
            An LLM writes an OpenSCAD model of this scan feature by feature, building and comparing each change with the scan. Runs are
            charged to the inference account of whoever starts them.
          </div>
        )}
        {jobs.length > 1 && (
          <Dropdown
            aria-label="Run"
            className="mb-2"
            value={jobId}
            onChange={(e) => setJobId(e.target.value)}
            minWidth={20}
            options={jobs.map((j) => ({
              value: j.id,
              label: `${new Date(j.created).toLocaleString()} · ${STATUS_LABEL[j.status] ?? j.status} · ${usd(j.cost_usd)}`,
            }))}
          />
        )}
        {job && (
          <>
            <div className="d-flex flex-wrap align-items-center gap-3 small mb-2" data-testid="agent-summary">
              <span>
                {usd(job.cost_usd)} of {usd(job.spec.budget_usd)}
                {job.cost_source === 'estimate' ? ' (est.)' : ''}
              </span>
              <span>{job.turns} turns</span>
              {job.revision > 0 && <span>revision {job.revision}</span>}
              {job.f_score > 0 && <span>F@1mm {job.f_score.toFixed(3)}</span>}
              {editor && agentRunning(job) && (
                <Button size="sm" variant="outline-danger" className="ms-auto" disabled={busy} onClick={() => void cancel()}>
                  <BsStopFill className="me-1" />
                  Cancel
                </Button>
              )}
            </div>
            {job.error && <div className="small text-danger mb-2">{job.error}</div>}
            {job.summary && <div className="small mb-2" style={{ whiteSpace: 'pre-wrap' }}>{job.summary}</div>}

            {job.plan && job.plan.length > 0 && <PlanChecklist plan={job.plan} />}
            {job.cuboids && job.cuboids.length > 0 && <InventoryList cuboids={job.cuboids} parts={job.inventory ?? []} />}
            <ol className="list-unstyled small mb-1" data-testid="agent-timeline">
              {shown.map((st) => (
                <li
                  key={st.n}
                  className={`d-flex align-items-start gap-2 py-1 border-bottom ${isSub(st) ? 'ps-3' : ''} ${selected === st.n ? 'bg-body-secondary' : ''}`}
                  data-testid={`step-${st.n}`}
                >
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 text-start text-decoration-none flex-grow-1"
                    onClick={() => setSelected(selected === st.n ? null : st.n)}
                    aria-expanded={selected === st.n}
                  >
                    <span className="text-body-secondary me-1">{st.n}.</span>
                    {isSub(st) && st.kind !== 'subagent_start' && st.kind !== 'subagent_done' && (
                      <span className="text-body-secondary me-1">[{subFeature(st)}]</span>
                    )}
                    {stepLabel(st)}
                    {st.f_score ? <span className="text-body-secondary"> · F {st.f_score.toFixed(3)}</span> : null}
                    {st.text && st.kind !== 'build' ? <span className="text-body-secondary"> · {clip(st.text, 80)}</span> : null}
                  </Button>
                  {st.images && st.images.length > 0 && (
                    <img src={urls.agentImage(scan.id, job.id, st.images[0])} alt="" style={{ width: '4.5rem' }} className="rounded" />
                  )}
                </li>
              ))}
            </ol>
            {steps.length > shown.length || showAll ? (
              <Button variant="link" size="sm" className="p-0" onClick={() => setShowAll(!showAll)}>
                {showAll ? 'Show key steps' : `Show all ${steps.length} steps`}
              </Button>
            ) : null}
            {sel && <StepInspector scan={scan} job={job} step={sel} fileAt={fileAt(sel.n)} />}

            <div className="d-flex flex-wrap gap-1 mt-2">
              <Button size="sm" variant="outline-secondary" as="a" href={urls.agentModelScad(scan.id, job.id)} download={`${base}.scad`} disabled={!steps.some((s) => s.file)}>
                <BsDownload className="me-1" />
                model.scad
              </Button>
              {job.revision > 0 && (
                <>
                  <Button size="sm" variant="outline-secondary" as="a" href={urls.cadStl(scan.id, job.revision)} download={`${base}-rev${job.revision}.stl`}>
                    <BsDownload className="me-1" />
                    STL
                  </Button>
                  <Button size="sm" variant="outline-secondary" as="a" href={urls.cadStep(scan.id, job.revision)} download={`${base}-rev${job.revision}.step`}>
                    <BsDownload className="me-1" />
                    STEP
                  </Button>
                </>
              )}
            </div>
          </>
        )}
      </Card.Body>
      <Card.Footer>
        {!signedIn ? (
          <span className="small text-body-secondary">Sign in to run the CAD agent on this scan.</span>
        ) : !editor ? (
          <span className="small text-body-secondary">You have view access; only the owner and editors can run the CAD agent.</span>
        ) : !inference ? (
          <div className="d-flex flex-wrap align-items-center gap-2" data-testid="inference-required">
            <span className="small text-body-secondary me-auto">
              The CAD agent calls an LLM on inference.lab0.us and charges it to your inference account, which needs the inference.use
              permission.
            </span>
            <Button size="sm" variant="outline-primary" as="a" href={inferenceLoginUrl(currentPath(), hasCompute(me))}>
              <BsBoxArrowInRight className="me-1" />
              Connect inference account
            </Button>
          </div>
        ) : running ? (
          <span className="small text-body-secondary">A run is in progress; one run per scan at a time.</span>
        ) : (
          <Form
            data-testid="agent-form"
            onSubmit={(e) => {
              e.preventDefault();
              void start();
            }}
          >
            <div className="d-flex flex-wrap gap-2 mb-2">
              <Form.Group controlId="agent-model" className="d-flex flex-column flex-grow-1">
                <Form.Label className="small mb-0">Model</Form.Label>
                <Dropdown
                  id="agent-model"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  minWidth={12}
                  options={modelOptions.map((m) => ({ value: m.id, label: `${m.label} (${priceOf(m)})` }))}
                />
              </Form.Group>
              {models.some((m) => m.vision) && (
                <Form.Group controlId="agent-inventory-model" className="d-flex flex-column flex-grow-1">
                  <Form.Label className="small mb-0" title="The model that names every part (key, port, screen) from close-up photos before the features are built">
                    Find parts with
                  </Form.Label>
                  <Dropdown
                    id="agent-inventory-model"
                    value={invModel}
                    onChange={(e) => setInvModel(e.target.value)}
                    minWidth={12}
                    options={[
                      { value: '', label: 'Same model' },
                      ...models.filter((m) => m.vision).map((m) => ({ value: m.id, label: `${m.label} (${priceOf(m)})` })),
                    ]}
                  />
                </Form.Group>
              )}
              <Form.Group controlId="agent-budget" className="d-flex flex-column">
                <Form.Label className="small mb-0">Budget</Form.Label>
                <Dropdown
                  id="agent-budget"
                  value={String(budget)}
                  onChange={(e) => setBudget(Number(e.target.value))}
                  minWidth={5}
                  options={BUDGETS.map((b) => ({ value: String(b), label: `$${b.toFixed(2)}` }))}
                />
              </Form.Group>
              {revisions.length > 0 && (
                <Form.Group controlId="agent-start-from" className="d-flex flex-column">
                  <Form.Label className="small mb-0">Start from</Form.Label>
                  <Dropdown
                    id="agent-start-from"
                    value={String(startFrom)}
                    onChange={(e) => setStartFrom(Number(e.target.value))}
                    minWidth={10}
                    options={[
                      { value: '0', label: 'Empty file' },
                      ...[...revisions].reverse().map((r) => ({
                        value: String(r.rev),
                        label: `Revision ${r.rev}${r.name ? ` · ${r.name}` : ''} (${r.source})`,
                      })),
                    ]}
                  />
                </Form.Group>
              )}
            </div>
            <Form.Group controlId="agent-instructions" className="mb-2">
              <Form.Label className="small mb-0">Instructions</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                size="sm"
                placeholder="What the object is and what matters, e.g. “a pair of flush cutters; model the grip ridges and the blade tilt”"
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
              />
            </Form.Group>
            <div className="d-flex flex-wrap align-items-center gap-2">
              <Button size="sm" variant="outline-primary" type="submit" disabled={busy}>
                <BsPlayFill className="me-1" />
                Start
              </Button>
              <span className="small text-body-secondary">
                Charged to {me?.email ? `${me.email}'s` : 'your'} inference account, up to ${budget.toFixed(2)}.
                {foreign ? ` This is ${scan.owner}'s scan.` : ''}
              </span>
            </div>
          </Form>
        )}
      </Card.Footer>
    </Card>
  );
}
